// BLOQUE 59 — Selección múltiple de filas en la Estructura de Cómputo.
// Utilidades puras (sin estado): expansión de selección (sección→hijos),
// movimiento de bloque (subir/bajar/drag) y validación de jerarquía.
// NO tocan calc_item/calculate_budget/computeItemLive — solo ordenamiento.

/**
 * Devuelve el índice de la última fila que pertenece al "subárbol" de una
 * sección/sub-sección (todas las filas siguientes hasta la próxima fila
 * is_group con nivel_jerarquia <= la de este grupo, o el final del array).
 */
export function getSubtreeEndIdx(items, idx) {
  const it = items[idx];
  if (!it || !it.is_group) return idx;
  const lvl = Number(it.nivel_jerarquia || 0);
  let end = idx;
  for (let i = idx + 1; i < items.length; i++) {
    const cur = items[i];
    if (cur.is_group && Number(cur.nivel_jerarquia || 0) <= lvl) break;
    end = i;
  }
  return end;
}

/**
 * Si la selección incluye una sección/sub-sección, incluye automáticamente
 * todos sus ítems hijos (subárbol completo). Devuelve el Set expandido y si
 * hubo cambios (para mostrar el aviso inline "incluye los ítems...").
 */
export function expandSelectionWithChildren(items, selectedIds) {
  const expanded = new Set(selectedIds);
  let changed = false;
  items.forEach((it, idx) => {
    if (it.is_group && selectedIds.has(it.__id)) {
      const end = getSubtreeEndIdx(items, idx);
      for (let i = idx + 1; i <= end; i++) {
        const childId = items[i].__id;
        if (!expanded.has(childId)) { expanded.add(childId); changed = true; }
      }
    }
  });
  return { expanded, changed };
}

/** Cuenta ítems (no-grupo) que quedan sin ninguna sección/sub-sección por encima. */
export function countOrphanItems(items) {
  let count = 0;
  let anyGroupSeen = false;
  for (const it of items) {
    if (it.is_group) { anyGroupSeen = true; continue; }
    if (!anyGroupSeen) count++;
  }
  return count;
}

/** Inserta el bloque seleccionado (en su orden relativo original) justo ANTES del target. */
export function moveSelectedBlockBeforeTarget(items, selectedIds, targetId) {
  if (selectedIds.has(targetId)) return items; // soltado dentro de su propio bloque: no-op
  const selected = items.filter((it) => selectedIds.has(it.__id));
  const rest = items.filter((it) => !selectedIds.has(it.__id));
  let insertAt = rest.findIndex((it) => it.__id === targetId);
  if (insertAt === -1) insertAt = rest.length;
  return [...rest.slice(0, insertAt), ...selected, ...rest.slice(insertAt)];
}

/** Inserta el bloque seleccionado justo DESPUÉS del target. */
export function moveSelectedBlockAfterTarget(items, selectedIds, targetId) {
  if (selectedIds.has(targetId)) return items;
  const selected = items.filter((it) => selectedIds.has(it.__id));
  const rest = items.filter((it) => !selectedIds.has(it.__id));
  let idx = rest.findIndex((it) => it.__id === targetId);
  if (idx === -1) idx = rest.length - 1;
  const insertAt = idx + 1;
  return [...rest.slice(0, insertAt), ...selected, ...rest.slice(insertAt)];
}

/**
 * Movimiento de bloque vía drag & drop: decide antes/después del target según
 * si el target está por encima o por debajo del tope del bloque seleccionado
 * (mismo criterio que `arrayMove` de dnd-kit para una sola fila).
 */
export function moveSelectedBlockToTarget(items, selectedIds, targetId) {
  if (selectedIds.has(targetId)) return items;
  const idxs = [];
  items.forEach((it, i) => { if (selectedIds.has(it.__id)) idxs.push(i); });
  if (!idxs.length) return items;
  const targetIdx = items.findIndex((it) => it.__id === targetId);
  if (targetIdx === -1) return items;
  const movingDown = targetIdx > idxs[idxs.length - 1];
  return movingDown
    ? moveSelectedBlockAfterTarget(items, selectedIds, targetId)
    : moveSelectedBlockBeforeTarget(items, selectedIds, targetId);
}

/**
 * Movimiento de bloque vía botones Subir/Bajar: mueve el bloque completo un
 * "paso" (pasa la única fila no-seleccionada más cercana), agrupando ítems
 * no consecutivos en el destino y conservando su orden relativo entre sí.
 */
export function moveSelectedBlockStep(items, selectedIds, direction) {
  const idxs = [];
  items.forEach((it, i) => { if (selectedIds.has(it.__id)) idxs.push(i); });
  if (!idxs.length) return items;
  if (direction === "up") {
    const top = idxs[0];
    if (top <= 0) return items; // ya está en el tope
    const pivot = items[top - 1];
    return moveSelectedBlockBeforeTarget(items, selectedIds, pivot.__id);
  }
  const bottom = idxs[idxs.length - 1];
  if (bottom >= items.length - 1) return items; // ya está al fondo
  const pivot = items[bottom + 1];
  return moveSelectedBlockAfterTarget(items, selectedIds, pivot.__id);
}
